/**
 * UI 문자열 다국어 지원 유틸리티
 */

/**
 * 문자열 내 {key} 플레이스홀더를 context 값으로 치환합니다.
 */
function interpolate(template: string, context?: Record<string, unknown>): string {
  if (!context) return template;

  return template.replace(/\{(\w+)\}/g, (match, key) => {
    const value = context[key];
    return value !== undefined ? String(value) : match;
  });
}

/**
 * 로케일 메시지를 반환합니다. context가 있으면 interpolation을 수행합니다.
 *
 * @param message - 로케일 객체에서 직접 참조한 메시지 문자열
 * @param context - interpolation용 컨텍스트 객체 (예: { name: 'MyStrategy' })
 * @returns 로컬라이즈된 메시지
 */
export function t(message: string, context?: Record<string, unknown>): string {
  if (!message) return '';
  return context ? interpolate(message, context) : message;
}

/**
 * 현재 선택된 locale을 저장하고 관리하는 모듈
 * React Context 외부에서 현재 locale에 접근해야 하는 순수 함수(유틸)에서 사용
 */

import type { SupportedLocale, LocaleResources } from '../locales/types';
import * as koResources from '../locales/ko';
import * as enResources from '../locales/en';

/**
 * 언어별 리소스 맵
 */
const localeResourcesMap: Record<SupportedLocale, LocaleResources> = {
  ko: koResources,
  en: enResources,
};

/**
 * 현재 선택된 locale을 저장하는 전역 변수
 * LocaleProvider에서만 변경해야 함
 */
let currentLocale: SupportedLocale = 'ko';

/**
 * 현재 locale 설정
 * @internal LocaleProvider에서만 사용해야 함
 */
export function setCurrentLocale(locale: SupportedLocale): void {
  currentLocale = locale;
}

/**
 * 현재 locale 반환
 */
export function getCurrentLocale(): SupportedLocale {
  return currentLocale;
}

/**
 * 현재 locale의 리소스 반환
 * 순수 함수(유틸)에서 현재 언어의 리소스가 필요할 때 사용
 *
 * @example
 * ```typescript
 * // error-utils.ts
 * import { getCurrentLocaleResources } from './i18n';
 *
 * export function getAppErrorMessage(code: ErrorCode, ctx?: ErrorContext): string {
 *   const { ErrorMessages } = getCurrentLocaleResources();
 *   const template = ErrorMessages[code] ?? code;
 *   return formatWithContext(template, ctx);
 * }
 * ```
 */
export function getCurrentLocaleResources(): LocaleResources {
  return localeResourcesMap[currentLocale];
}
